import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../hooks/toast';
import {
  IGuide,
  IClient,
  IGuideStatus,
  ICovenant,
  IProfessional,
  IPaymentMethod,
} from '../../shared/interfaces';
import { Tag, Typography } from 'antd';
import {
  ATable,
  MenuOptions,
  DropdownOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import {
  DollarOutlined,
  EditOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  HistoryOutlined,
  IssuesCloseOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import GuideCreateEdit from './CreateEdit';
import {
  CurrencyText,
  GuidesLayout,
  GuidesSummaryCard,
  GuidesSummaryGrid,
} from './styles';
import { currency } from '../../shared/utils/functions';
import HistoricGuide from './Historic';
import GenerateFinancial from './GenerateFinancial';
import {
  getGuides,
  getSelectClients,
  getSelectCovenants,
  getSelectGuidesStatus,
  getSelectPaymentsMethods,
  getSelectProfessionals,
} from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const { Text } = Typography;

const Guides: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [guide, setGuide] = useState<IGuide | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [modalHistoric, setModalHistoric] = useState<boolean>(false);
  const [modalFinancial, setModalFinancial] = useState<boolean>(false);
  const [guidesStatus, setGuidesStatus] = useState<IGuideStatus[]>([]);
  const [payments, setPayments] = useState<IPaymentMethod[]>([]);
  const [guides, setGuides] = useState<IGuide[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();
  const [clients, setClients] = useState<IClient[]>([]);
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [covenants, setCovenants] = useState<ICovenant[]>([]);

  const guidesSummary = useMemo(() => {
    const total = guides.length;
    const generatedFinancial = guides.filter(
      (item) => item.generate_financial,
    ).length;
    const pendingFinancial = total - generatedFinancial;
    const totalValue = guides.reduce(
      (acc, item) => acc + Number(item.value || 0),
      0,
    );

    return {
      total,
      generatedFinancial,
      pendingFinancial,
      totalValue,
    };
  }, [guides]);

  const getStatusInfo = useCallback(
    (statusId: string) => guidesStatus.find((item) => item.id === statusId),
    [guidesStatus],
  );

  const listGuides = useCallback(async () => {
    setLoading(true);
    setGuides([]);
    try {
      const response = await getGuides(limit, (page - 1) * limit, search);
      setPages(response.count);
      setGuides(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar guias',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const listProfessionals = useCallback(async () => {
    try {
      const data = await getSelectProfessionals();
      setProfessionals(data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar os profissionais',
      });
    }
  }, [addToast]);

  const loadClients = useCallback(async () => {
    await getSelectClients(1)
      .then((data) => setClients(data))
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `${
            error.response.data.error ?? 'Erro ao listar os clientes.'
          }`,
        });
      });
  }, []);

  const loadGuidesStatus = useCallback(async () => {
    await getSelectGuidesStatus()
      .then((data) => setGuidesStatus(data))
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `${
            error.response.data.error ?? 'Erro ao listar status guias'
          }`,
        });
      });
  }, []);

  const listCovenants = useCallback(async () => {
    await getSelectCovenants()
      .then((data) => setCovenants(data))
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: 'Erro ao listar convênios',
        });
      });
  }, []);

  const listPaymentsMethods = useCallback(async () => {
    await getSelectPaymentsMethods()
      .then((data) => setPayments(data))
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: 'Erro ao listar formas de pagamento',
        });
      });
  }, []);

  const renderModalGuideCreateEdit = () => {
    if (modalCreate)
      return (
        <GuideCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          guide={guide}
          setGuides={setGuides}
          clients={clients}
          professionals={professionals}
          guidesStatus={guidesStatus}
          covenants={covenants}
        />
      );
  };

  const renderModalGuideHistoric = () => {
    if (modalHistoric)
      return (
        <HistoricGuide
          open={modalHistoric}
          close={() => setModalHistoric(false)}
          guide={guide}
        />
      );
  };

  const renderModalGenerateFinancial = () => {
    if (modalFinancial)
      return (
        <GenerateFinancial
          open={modalFinancial}
          close={() => setModalFinancial(false)}
          guide={guide}
          payments={payments}
          setGuides={setGuides}
        />
      );
  };

  useEffect(() => {
    loadClients();
    listProfessionals();
    listCovenants();
    loadGuidesStatus();
    listPaymentsMethods();
  }, []);

  useEffect(() => {
    listGuides();
  }, [page, search, limit, listGuides]);

  return (
    <GuidesLayout>
      <PageIntro
        badge="Guias"
        icon={<SnippetsOutlined />}
        title="Guias"
        description="Acompanhe o volume de guias emitidas, situação financeira e valor total da página atual com mais clareza."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listGuides(),
          },
          permissionFind([PermissionActions.guidesCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setGuide(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />

      <GuidesSummaryGrid>
        <GuidesSummaryCard>
          <Text className="label" type="secondary">
            Guias na página
          </Text>
          <div className="value">{guidesSummary.total}</div>
        </GuidesSummaryCard>
        <GuidesSummaryCard>
          <Text className="label" type="secondary">
            Financeiro gerado
          </Text>
          <div className="value success">
            {guidesSummary.generatedFinancial}
          </div>
        </GuidesSummaryCard>
        <GuidesSummaryCard>
          <Text className="label" type="secondary">
            Pendente financeiro
          </Text>
          <div className="value warning">{guidesSummary.pendingFinancial}</div>
        </GuidesSummaryCard>
        <GuidesSummaryCard>
          <Text className="label" type="secondary">
            Valor total listado
          </Text>
          <div className="value currency">
            {currency(guidesSummary.totalValue)}
          </div>
        </GuidesSummaryCard>
      </GuidesSummaryGrid>

      <PagePanel>
        <ATable
          columns={[
            {
              title: 'Id',
              dataIndex: 'code',
              width: 85,
              render: (value: string | number) => <Text strong>{value}</Text>,
            },
            {
              title: 'Cliente',
              dataIndex: 'client_name',
              render: (value: string) => value || '-',
            },
            {
              title: 'Profissional',
              dataIndex: 'professional_name',
              render: (value: string) => value || '-',
            },
            {
              title: 'Valor',
              dataIndex: 'value',
              width: 140,
              render: (value: string | number) => (
                <CurrencyText>{currency(value)}</CurrencyText>
              ),
            },
            {
              title: 'Nº Guia',
              dataIndex: 'guide_number',
              render: (value: string) => <Text>{value || '-'}</Text>,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (value: string) => {
                const statusInfo = getStatusInfo(value);

                return (
                  <Tag
                    color={statusInfo?.color || 'default'}
                    style={{ marginRight: 0, fontWeight: 600 }}
                  >
                    {statusInfo?.description || 'Não informado'}
                  </Tag>
                );
              },
            },
            {
              title: 'Financeiro Gerado',
              dataIndex: 'financial_generated',
              render: (value: boolean) => (
                <Tag
                  color={value ? 'success' : 'warning'}
                  icon={
                    value ? <IssuesCloseOutlined /> : <FolderOpenOutlined />
                  }
                  style={{ marginRight: 0, fontWeight: 600 }}
                >
                  {value ? 'Gerado' : 'Pendente'}
                </Tag>
              ),
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IGuide) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.guidesUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setGuide(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                    {
                      label: 'Histórico',
                      key: '2',
                      icon: <HistoryOutlined />,
                      onClick: () => {
                        setGuide(cell);
                        setModalHistoric(true);
                      },
                    },
                    permissionFind([PermissionActions.guidesUpdate]) &&
                    permissionFind([PermissionActions.financialsCreate])
                      ? {
                          label: 'Gerar Financeiro',
                          key: '3',
                          icon: <DollarOutlined />,
                          onClick: () => {
                            setGuide(cell);
                            setModalFinancial(true);
                          },
                          disabled: cell.generate_financial,
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={guides}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
          locale={{
            emptyText: (
              <div style={{ padding: '18px 0' }}>
                <FileTextOutlined style={{ fontSize: 18, marginBottom: 8 }} />
                <div>Nenhuma guia encontrada para os filtros informados.</div>
              </div>
            ),
          }}
        />
      </PagePanel>
      {renderModalGuideCreateEdit()}
      {renderModalGuideHistoric()}
      {renderModalGenerateFinancial()}
    </GuidesLayout>
  );
};

export default Guides;
